import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import * as _ from 'lodash';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'providers/auth.service';
import { ConfigService } from 'providers/config.service';

// Define the format of a User Object (from API for others to use).
// With defaults, so any missing field will still be available.
// Main benefit: type and typo checking when others are dealing with users returned by get or getAll from here.
export interface UserFromAPI {
    createdAt: string;
    email: string;
    modifiedAt: string;
    name: string;
    position: string;
    department: string;
    tenantId: string;
    secondaryTenantIds: string[];
    picture: string;
    systemRoles: string[];
    roles: string[];
    userId: string;
    language: string;
    userRole: string;
    organizationName: string;
}

@Injectable()
export class IamUsersApiService {
    private token: any;
    private apiUrl = {
        users: '',
        adminUsers: '',
    };
    private cache: { all: UserFromAPI[]; single: Map<string, UserFromAPI> } = {
        all: null,
        single: new Map(),
    };

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {
        this.apiUrl.users = `${this.config.restApiUrl}/iam/users`;
        this.apiUrl.adminUsers = `${this.config.restApiUrl}/iam/admin/users`;
    }

    get httpOptions() {
        const options = { headers: { Authorization: `Bearer ${this.authService.jwt}` } };
        return options;
    }

    getAll(cache = true): Observable<UserFromAPI[]> {
        return new Observable((observer) => {
            // PERFORMANCE: First return previous (cached) to give immediate response...
            if (this.cache.all && !!cache) {
                observer.next(this.cache.all);
            }
            this.http
                .get<UserFromAPI[]>(this.apiUrl.users, this.httpOptions)
                .pipe(
                    catchError((err) => {
                        this.getUserServiceErrorMessage(err); // log error

                        return of([]); // return empty array
                    })
                )
                .subscribe((result) => {
                    this.cache.all = result;
                    // ...then, return the fresh data (component will update if needed)
                    observer.next(result);
                });
        });
    }

    getFromCache(userId: string): UserFromAPI {
        return (this.cache.all || []).find((user) => user.userId === userId);
    }

    create(user: UserFromAPI): Promise<any> {
        // Extend the object with the password field.
        // Keeping it out of all other methods, just in case.
        return this.http
            .post<any>(this.apiUrl.users, JSON.stringify(user), this.httpOptions)
            .pipe(catchError(this.getUserServiceErrorMessage))
            .toPromise();
    }

    save(user: UserFromAPI): Promise<any> {
        return this.http
            .put<any>(this.apiUrl.users + '/' + encodeURI(user.userId), JSON.stringify(user), this.httpOptions)
            .pipe(catchError(this.getUserServiceErrorMessage))
            .toPromise();
    }

    delete(user: UserFromAPI): Observable<Response> {
        return this.http.delete<any>(this.apiUrl.users + '/' + encodeURI(user.userId), this.httpOptions);
    }

    // internal users/ admin actions
    async getAllForOrganization(orgId: string) {
        return new Promise<UserFromAPI[]>((resolve) => {
            this.http
                .get<UserFromAPI[]>(this.apiUrl.adminUsers, this.httpOptions)
                .toPromise()
                .then((result) => {
                    const users = result.filter((u) => (u.tenantId || '').toLowerCase() === orgId.toLowerCase());
                    resolve(users);
                })
                .catch((e) => {
                    console.error(e);
                    resolve([]);
                });
        });
    }

    getAllAdmin(): Observable<UserFromAPI[]> {
        return new Observable((observer) => {
            this.http.get<UserFromAPI[]>(this.apiUrl.adminUsers, this.httpOptions).subscribe((result) => {
                // ...then, return the fresh data (component will update if needed)
                observer.next(result);
            });
        });
    }

    getAdmin(userId: string, tenantId: string): Observable<UserFromAPI> {
        return new Observable((observer) => {
            const url = `${this.apiUrl.adminUsers}/${encodeURIComponent(userId)}?tenantId=${encodeURIComponent(tenantId)}`;
            this.http.get<UserFromAPI>(url, this.httpOptions).subscribe((result) => {
                observer.next(result);
                observer.complete();
            });
        });
    }

    saveAdmin(user: UserFromAPI, tenantId: string): Promise<any> {
        const url = `${this.apiUrl.adminUsers}/${encodeURIComponent(user.userId)}?tenantId=${encodeURIComponent(tenantId)}`;
        return this.http
            .put<any>(url, JSON.stringify(user), this.httpOptions)
            .pipe(catchError(this.getUserServiceErrorMessage))
            .toPromise();
    }

    createAdmin(user: UserFromAPI, password: string, tenantId: string): Promise<UserFromAPI> {
        const newUser = { ...user };
        // Extend the object with the password field.
        // Keeping it out of all other methods, just in case.
        newUser['password'] = password;
        const url = `${this.apiUrl.adminUsers}?tenantId=${encodeURIComponent(tenantId)}`;
        return this.http
            .post<any>(url, JSON.stringify(newUser), this.httpOptions)
            .pipe(catchError(this.getUserServiceErrorMessage))
            .toPromise();
    }

    deleteAdmin(user: UserFromAPI, tenantId: string): Promise<Response> {
        const url = `${this.apiUrl.adminUsers}/${encodeURIComponent(user.userId)}?tenantId=${encodeURIComponent(tenantId)}`;
        return this.http.delete<Response>(url, this.httpOptions).toPromise();
    }

    /**
     * Error handler
     */
    getUserServiceErrorMessage(error: HttpErrorResponse): any {
        let message: any = error;
        console.error(message);
        if (error && error.error) {
            message = error.error;

            // keep http status code
            if (error.status) {
                _.set(message, 'status', error.status);
            }
        }

        return throwError(message);
    }
}
